import { useRef } from 'react';
import { m, useReducedMotion, useScroll, useTransform, useMotionValue, useSpring } from 'framer-motion';
import HexShape from './HexShape';

export default function About() {
  const shouldReduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });

  const mouseX = useMotionValue(50);
  const springX = useSpring(mouseX, { stiffness: 40, damping: 18 });

  const handleMouseMove = (e) => {
    if (shouldReduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    mouseX.set(((e.clientX - r.left) / r.width) * 100);
  };

  const h1y = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const h2y = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const h1x = useTransform(springX, [0, 100], [10, -10]);
  const h2x = useTransform(springX, [0, 100], [-8, 8]);

  return (
    <section
      ref={ref}
      id="about"
      className="bg-surface py-28 sm:py-36 relative overflow-hidden"
      onMouseMove={handleMouseMove}
    >
      <div className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
        <m.div
          className="absolute"
          style={{ top: '5%', right: '-5%', x: shouldReduce ? 0 : h1x, y: shouldReduce ? 0 : h1y }}
        >
          <HexShape size={280} className="text-primary" style={{ opacity: 0.13 }} />
        </m.div>
        <m.div
          className="absolute"
          style={{ bottom: '-6%', left: '40%', x: shouldReduce ? 0 : h2x, y: shouldReduce ? 0 : h2y }}
        >
          <HexShape size={160} className="text-accent" style={{ opacity: 0.14 }} />
        </m.div>
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          <m.div
            initial={shouldReduce ? false : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="text-primary text-xs tracking-[0.2em] uppercase font-medium mb-8">
              About
            </p>
            <h2
              className="text-ink font-extrabold leading-[0.93] tracking-tight"
              style={{ fontSize: 'clamp(2rem, 3.5vw, 3.5rem)' }}
            >
              Every business<br />is different.
            </h2>
            <m.div
              className="mt-10 h-px w-16 bg-accent"
              style={{ originX: 0 }}
              initial={shouldReduce ? false : { scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
            />
          </m.div>

          <m.div
            initial={shouldReduce ? false : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.65, delay: shouldReduce ? 0 : 0.14, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-6 text-ink-muted leading-relaxed lg:pt-16"
          >
            <p>
              The way you work, the people you serve, the problems you solve. They're
              yours alone. Generic software doesn't understand that.
            </p>
            <p>
              Vehi Systems was founded on a simple passion: building beautiful digital
              products that genuinely fit the businesses they serve. Custom software
              crafted around the people using it every day.
            </p>
            <p>
              We start by listening. We learn your business, understand your goals, and
              then build something made entirely around you. Software that works the way
              you work. Software your team loves to use.
            </p>
            <p className="text-ink/80 font-medium">
              Not a vendor. Not a contractor. The person in the room who actually
              understands your business.
            </p>
          </m.div>
        </div>
      </div>
    </section>
  );
}
